import {
  RobotoBoardRevisionConflictException,
  RobotoNotFoundException,
} from "@/shared/http";

import { newBoardDraftId } from "./boardDraftId";
import {
  boardDefinitionTitle,
  type BoardDraft,
  type UpdateBoardRequest,
} from "./BoardRecord";

const DRAFT_KEY_PREFIX = "roboto.boards.draft";

/**
 * How long a draft is kept after its last write. The metrics dashboard keeps
 * its drafts for the same time.
 */
const DRAFT_LIFETIME_MS = 15 * 24 * 60 * 60 * 1000;

/** What a new draft starts from. */
export interface CreateBoardDraftRequest {
  board_definition: Record<string, unknown>;
  /**
   * The draft's name, or `null` for none. A definition with a title takes a
   * given name as its title, and gives its title as the name when none is given.
   */
  name: string | null;
}

/**
 * One person's board drafts, kept in a `Storage`, one entry
 * per draft, so a draft is there when the person comes back and in their other
 * tabs.
 *
 * Writes follow the rules the platform applies to a stored board, so the code
 * that writes a board behaves the same for a draft:
 *
 * - A write of definition content advances `revision`. A write that carries a
 *   base revision other than the draft's is refused with
 *   `RobotoBoardRevisionConflictException`, which carries the draft as kept,
 *   so a tab cannot erase an edit another tab made since it read the draft.
 * - A draft's name and a non-empty definition title are kept equal.
 * - A draft that is not there is a `RobotoNotFoundException`.
 *
 * A write the browser's storage refuses, as it does when it is full, throws,
 * and the draft stays as it was. Draft names are not unique: the platform
 * applies its rule when the draft is saved.
 */
export class BoardDraftStore {
  #userId: string;
  #storage: Storage;

  /**
   * @param userId Whose drafts these are. Another person signed in on the
   *   same browser gets none of them.
   * @param storage Where the drafts are kept: the browser's `localStorage`
   *   in the app.
   */
  constructor(userId: string, storage: Storage) {
    this.#userId = userId;
    this.#storage = storage;
  }

  public create(request: CreateBoardDraftRequest, orgId: string): BoardDraft {
    const definition =
      request.name !== null &&
      boardDefinitionTitle(request.board_definition) !== null
        ? { ...request.board_definition, title: request.name }
        : request.board_definition;
    const draft: BoardDraft = {
      board_definition: definition,
      board_id: newBoardDraftId(),
      draft: true,
      modified: new Date().toISOString(),
      name: boardDefinitionTitle(definition) ?? request.name,
      org_id: orgId,
      revision: 0,
    };
    this.#write(draft);
    return draft;
  }

  public delete(boardId: string, orgId: string): void {
    this.#storage.removeItem(this.#key(boardId, orgId));
  }

  public get(boardId: string, orgId: string): BoardDraft {
    const draft = readDraft(this.#storage, this.#key(boardId, orgId));
    if (draft === null) {
      throw new RobotoNotFoundException({
        error_code: RobotoNotFoundException.name,
        message: `Board ${boardId} not found`,
      });
    }
    return draft;
  }

  /** The person's drafts in `orgId`, the most recently written first. */
  public list(orgId: string): BoardDraft[] {
    const ownKeyPrefix = this.#key("", orgId);
    const drafts: BoardDraft[] = [];
    // Every draft key is read, not only this person's, so that a draft of an
    // organization or a person this browser never opens again still expires.
    const keys = Array.from({ length: this.#storage.length }, (_, index) =>
      this.#storage.key(index),
    );
    for (const key of keys) {
      if (key === null || !key.startsWith(`${DRAFT_KEY_PREFIX}:`)) {
        continue;
      }
      const draft = readDraft(this.#storage, key);
      if (draft !== null && key.startsWith(ownKeyPrefix)) {
        drafts.push(draft);
      }
    }
    return drafts.sort(
      (a, b) => Date.parse(b.modified) - Date.parse(a.modified),
    );
  }

  public update(
    boardId: string,
    request: UpdateBoardRequest,
    orgId: string,
  ): BoardDraft {
    const existing = this.get(boardId, orgId);
    if (
      request.base_revision !== undefined &&
      request.base_revision !== existing.revision
    ) {
      const refusal = {
        error_code: RobotoBoardRevisionConflictException.name,
        message: "This board was changed in another tab since you loaded it.",
        current_board: existing,
      };
      throw new RobotoBoardRevisionConflictException(refusal);
    }
    const next = {
      ...(request.board_definition === undefined
        ? renamed(existing, request.name)
        : withDefinition(existing, request.board_definition)),
      modified: new Date().toISOString(),
    };
    this.#write(next);
    return next;
  }

  #key(boardId: string, orgId: string): string {
    return `${DRAFT_KEY_PREFIX}:${orgId}:${this.#userId}:${boardId}`;
  }

  #write(draft: BoardDraft): void {
    this.#storage.setItem(
      this.#key(draft.board_id, draft.org_id),
      JSON.stringify(draft),
    );
  }
}

/**
 * The draft kept under `key`, or `null` when there is none. An entry that is
 * not JSON, or a draft last written more than `DRAFT_LIFETIME_MS` ago, is
 * removed and reported as none.
 */
function readDraft(storage: Storage, key: string): BoardDraft | null {
  const text = storage.getItem(key);
  if (text === null) {
    return null;
  }
  const draft = parsedDraft(text);
  if (
    draft === null ||
    Date.now() - Date.parse(draft.modified) > DRAFT_LIFETIME_MS
  ) {
    storage.removeItem(key);
    return null;
  }
  return draft;
}

/** The draft `text` holds, or `null` when it is not JSON. */
function parsedDraft(text: string): BoardDraft | null {
  try {
    return JSON.parse(text) as BoardDraft;
  } catch {
    return null;
  }
}

/** `existing` with a replacement definition, named after its title when it
 *  has one, at the next revision. */
function withDefinition(
  existing: BoardDraft,
  definition: Record<string, unknown>,
): BoardDraft {
  return {
    ...existing,
    board_definition: definition,
    name: boardDefinitionTitle(definition) ?? existing.name,
    revision: existing.revision + 1,
  };
}

/** `existing` under another name, which a titled definition's title follows
 *  at the next revision. */
function renamed(existing: BoardDraft, name: string): BoardDraft {
  if (boardDefinitionTitle(existing.board_definition) === null) {
    return { ...existing, name };
  }
  return {
    ...existing,
    name,
    board_definition: { ...existing.board_definition, title: name },
    revision: existing.revision + 1,
  };
}
